import { useEffect, useRef } from "react";
import maplibregl, {
	type GeoJSONSource,
	type Map,
	type MapLayerMouseEvent,
	type MapMouseEvent,
	type MapTouchEvent,
	type StyleSpecification,
} from "maplibre-gl";
import type { Entry, GeoJsonGeometry, GeomType } from "shared";
import type { DrawMode, ViewportTarget } from "./DrawToolbar";

const OPENFREEMAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";
const ENTRIES_SOURCE = "kartenwerk-entries";
const DRAFT_SOURCE = "kartenwerk-draft";
const LABELS_SOURCE = "kartenwerk-area-labels";
const LAYER_FILL = "kartenwerk-fill";
const LAYER_LINE = "kartenwerk-line";
const LAYER_POINT = "kartenwerk-point";
const LAYER_POINT_HIT = "kartenwerk-point-hit";
const LAYER_AREA_LABEL = "kartenwerk-area-label";
const LAYER_DRAFT_LINE = "kartenwerk-draft-line";
const LAYER_DRAFT_FILL = "kartenwerk-draft-fill";
const LAYER_DRAFT_POINT = "kartenwerk-draft-point";
const LAYER_DRAFT_MIDPOINT = "kartenwerk-draft-midpoint";
const LAYER_DRAFT_VERTEX_HIT = "kartenwerk-draft-vertex-hit";
const LAYER_DRAFT_MIDPOINT_HIT = "kartenwerk-draft-midpoint-hit";
const LABEL_IMAGE_PREFIX = "kw-area-label:";

type MapFeature = {
	type: "Feature";
	id?: string;
	properties: Record<string, string | number | boolean | null>;
	geometry: GeoJsonGeometry;
};

type FeatureCollection = {
	type: "FeatureCollection";
	features: MapFeature[];
};

export type MapLayerData = {
	id: string;
	title: string;
	color: string;
	visible: boolean;
	entries: Entry[];
};

export type ViewportCommand = ViewportTarget & { key: number };

type Props = {
	className?: string;
	center?: [number, number];
	zoom?: number;
	layers: MapLayerData[];
	drawMode: DrawMode;
	selectedEntryId: string | null;
	draftVertices: [number, number][];
	editing?: boolean;
	viewportCommand?: ViewportCommand | null;
	onSelectEntry: (entryId: string | null) => void;
	onMapClick: (lngLat: [number, number]) => void;
	onFinishDraft?: () => void;
	onVertexInsert?: (index: number, coord: [number, number]) => void;
	onVertexRemove?: (index: number) => void;
	onVertexDrag?: (index: number, coord: [number, number]) => void;
	onReady?: (map: Map) => void;
};

function entriesToFeatureCollection(
	layers: MapLayerData[],
	selectedEntryId: string | null,
): FeatureCollection {
	const features: MapFeature[] = [];
	for (const layer of layers) {
		if (!layer.visible) continue;
		for (const entry of layer.entries) {
			features.push({
				type: "Feature",
				id: entry.id,
				properties: {
					id: entry.id,
					layerId: layer.id,
					layerTitle: layer.title,
					title: entry.title,
					geomType: entry.geomType,
					color: layer.color,
					selected: entry.id === selectedEntryId ? 1 : 0,
				},
				geometry: entry.geometry,
			});
		}
	}
	return { type: "FeatureCollection", features };
}

function polygonLabelCenter(geometry: GeoJsonGeometry): [number, number] | null {
	const coords: number[][] = [];
	const walk = (node: unknown) => {
		if (!Array.isArray(node)) return;
		if (
			node.length >= 2 &&
			typeof node[0] === "number" &&
			typeof node[1] === "number"
		) {
			coords.push([node[0], node[1]]);
			return;
		}
		for (const child of node) walk(child);
	};
	walk(geometry.coordinates);
	if (coords.length === 0) return null;
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	for (const [lng, lat] of coords) {
		if (lng < minLng) minLng = lng;
		if (lat < minLat) minLat = lat;
		if (lng > maxLng) maxLng = lng;
		if (lat > maxLat) maxLat = lat;
	}
	return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

function areaLabelImageId(layerTitle: string): string {
	return `${LABEL_IMAGE_PREFIX}${layerTitle}`;
}

function createPillLabelImage(text: string, pixelRatio = 2): {
	width: number;
	height: number;
	data: Uint8Array;
	pixelRatio: number;
} {
	const padX = 10;
	const padY = 5;
	const fontSize = 12;
	const radius = 6;
	const border = "#d4d4d8";
	const fill = "#ffffff";
	const ink = "#52525b";

	const measure = document.createElement("canvas");
	const mctx = measure.getContext("2d");
	if (!mctx) {
		return { width: 1, height: 1, data: new Uint8Array(4), pixelRatio: 1 };
	}
	mctx.font = `500 ${fontSize}px "IBM Plex Mono", ui-monospace, monospace`;
	const textWidth = Math.ceil(mctx.measureText(text).width);
	const width = Math.max(textWidth + padX * 2, 28);
	const height = fontSize + padY * 2;

	const canvas = document.createElement("canvas");
	canvas.width = Math.ceil(width * pixelRatio);
	canvas.height = Math.ceil(height * pixelRatio);
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		return { width: 1, height: 1, data: new Uint8Array(4), pixelRatio: 1 };
	}
	ctx.scale(pixelRatio, pixelRatio);
	ctx.font = `500 ${fontSize}px "IBM Plex Mono", ui-monospace, monospace`;

	const x = 0.5;
	const y = 0.5;
	const w = width - 1;
	const h = height - 1;
	ctx.beginPath();
	ctx.moveTo(x + radius, y);
	ctx.arcTo(x + w, y, x + w, y + h, radius);
	ctx.arcTo(x + w, y + h, x, y + h, radius);
	ctx.arcTo(x, y + h, x, y, radius);
	ctx.arcTo(x, y, x + w, y, radius);
	ctx.closePath();
	ctx.fillStyle = fill;
	ctx.fill();
	ctx.strokeStyle = border;
	ctx.lineWidth = 1;
	ctx.stroke();

	ctx.fillStyle = ink;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText(text, width / 2, height / 2 + 0.5);

	const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
	return {
		width: canvas.width,
		height: canvas.height,
		data: new Uint8Array(imageData.data.buffer),
		pixelRatio,
	};
}

function ensureAreaLabelImages(map: Map, layers: MapLayerData[]) {
	const titles = new Set<string>();
	for (const layer of layers) {
		if (!layer.visible) continue;
		for (const entry of layer.entries) {
			if (entry.geomType === "polygon" && entry.title.trim()) {
				titles.add(entry.title);
			}
		}
	}
	for (const title of titles) {
		const id = areaLabelImageId(title);
		if (map.hasImage(id)) continue;
		const img = createPillLabelImage(title);
		map.addImage(
			id,
			{ width: img.width, height: img.height, data: img.data },
			{ pixelRatio: img.pixelRatio },
		);
	}
}

function areaLabelsFeatureCollection(layers: MapLayerData[]): FeatureCollection {
	const features: MapFeature[] = [];
	for (const layer of layers) {
		if (!layer.visible) continue;
		for (const entry of layer.entries) {
			if (entry.geomType !== "polygon") continue;
			const label = entry.title.trim();
			if (!label) continue;
			const center = polygonLabelCenter(entry.geometry);
			if (!center) continue;
			features.push({
				type: "Feature",
				id: `label-${entry.id}`,
				properties: {
					id: entry.id,
					icon: areaLabelImageId(label),
				},
				geometry: {
					type: "Point",
					coordinates: center,
				},
			});
		}
	}
	return { type: "FeatureCollection", features };
}

function draftToFeatureCollection(
	vertices: [number, number][],
	mode: DrawMode,
	editing = false,
): FeatureCollection {
	const features: MapFeature[] = [];
	if (vertices.length === 0) {
		return { type: "FeatureCollection", features };
	}

	for (let i = 0; i < vertices.length; i++) {
		features.push({
			type: "Feature",
			properties: { kind: "vertex", index: i },
			geometry: {
				type: "Point",
				coordinates: vertices[i],
			},
		});
	}

	if (vertices.length >= 2 && (mode === "line" || mode === "polygon")) {
		const lineCoords =
			mode === "polygon" && vertices.length >= 3
				? [...vertices, vertices[0]]
				: vertices;
		features.push({
			type: "Feature",
			properties: { kind: "line" },
			geometry: {
				type: "LineString",
				coordinates: lineCoords,
			},
		});
	}

	if (mode === "polygon" && vertices.length >= 3) {
		const ring = [...vertices, vertices[0]];
		features.push({
			type: "Feature",
			properties: { kind: "fill" },
			geometry: {
				type: "Polygon",
				coordinates: [ring],
			},
		});
	}

	if (editing && vertices.length >= 2 && (mode === "line" || mode === "polygon")) {
		const edgeCount =
			mode === "polygon" && vertices.length >= 3
				? vertices.length
				: vertices.length - 1;
		for (let i = 0; i < edgeCount; i++) {
			const a = vertices[i];
			const b = vertices[(i + 1) % vertices.length];
			features.push({
				type: "Feature",
				properties: { kind: "midpoint", index: i },
				geometry: {
					type: "Point",
					coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
				},
			});
		}
	}

	return { type: "FeatureCollection", features };
}

function ensureMapLayers(map: Map) {
	if (!map.getSource(ENTRIES_SOURCE)) {
		map.addSource(ENTRIES_SOURCE, {
			type: "geojson",
			data: { type: "FeatureCollection", features: [] },
		});
	}
	if (!map.getSource(DRAFT_SOURCE)) {
		map.addSource(DRAFT_SOURCE, {
			type: "geojson",
			data: { type: "FeatureCollection", features: [] },
		});
	}
	if (!map.getSource(LABELS_SOURCE)) {
		map.addSource(LABELS_SOURCE, {
			type: "geojson",
			data: { type: "FeatureCollection", features: [] },
		});
	}

	if (!map.getLayer(LAYER_FILL)) {
		map.addLayer({
			id: LAYER_FILL,
			type: "fill",
			source: ENTRIES_SOURCE,
			filter: ["==", ["geometry-type"], "Polygon"],
			paint: {
				"fill-color": ["coalesce", ["get", "color"], "#14b8a6"],
				"fill-opacity": [
					"case",
					["==", ["get", "selected"], 1],
					0.45,
					0.28,
				],
			},
		});
	}
	if (!map.getLayer(LAYER_LINE)) {
		map.addLayer({
			id: LAYER_LINE,
			type: "line",
			source: ENTRIES_SOURCE,
			filter: [
				"any",
				["==", ["geometry-type"], "LineString"],
				["==", ["geometry-type"], "Polygon"],
			],
			paint: {
				"line-color": ["coalesce", ["get", "color"], "#14b8a6"],
				"line-width": [
					"case",
					["==", ["get", "selected"], 1],
					4,
					2.5,
				],
			},
		});
	}
	if (!map.getLayer(LAYER_POINT_HIT)) {
		map.addLayer({
			id: LAYER_POINT_HIT,
			type: "circle",
			source: ENTRIES_SOURCE,
			filter: ["==", ["geometry-type"], "Point"],
			paint: {
				"circle-radius": 14,
				"circle-opacity": 0,
			},
		});
	}
	if (!map.getLayer(LAYER_POINT)) {
		map.addLayer({
			id: LAYER_POINT,
			type: "circle",
			source: ENTRIES_SOURCE,
			filter: ["==", ["geometry-type"], "Point"],
			paint: {
				"circle-radius": [
					"case",
					["==", ["get", "selected"], 1],
					8,
					6,
				],
				"circle-color": ["coalesce", ["get", "color"], "#14b8a6"],
				"circle-stroke-width": 2,
				"circle-stroke-color": "#ffffff",
			},
		});
	}
	if (!map.getLayer(LAYER_AREA_LABEL)) {
		map.addLayer({
			id: LAYER_AREA_LABEL,
			type: "symbol",
			source: LABELS_SOURCE,
			layout: {
				"icon-image": ["get", "icon"],
				"icon-allow-overlap": true,
				"icon-ignore-placement": true,
				"icon-anchor": "center",
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_FILL)) {
		map.addLayer({
			id: LAYER_DRAFT_FILL,
			type: "fill",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "fill"],
			paint: {
				"fill-color": "#14b8a6",
				"fill-opacity": 0.25,
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_LINE)) {
		map.addLayer({
			id: LAYER_DRAFT_LINE,
			type: "line",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "line"],
			paint: {
				"line-color": "#14b8a6",
				"line-width": 3,
				"line-dasharray": [2, 1],
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_MIDPOINT)) {
		map.addLayer({
			id: LAYER_DRAFT_MIDPOINT,
			type: "circle",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "midpoint"],
			paint: {
				"circle-radius": 4.5,
				"circle-color": "#ffffff",
				"circle-stroke-width": 2,
				"circle-stroke-color": "#14b8a6",
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_POINT)) {
		map.addLayer({
			id: LAYER_DRAFT_POINT,
			type: "circle",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "vertex"],
			paint: {
				"circle-radius": 6,
				"circle-color": "#14b8a6",
				"circle-stroke-width": 2,
				"circle-stroke-color": "#ffffff",
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_VERTEX_HIT)) {
		map.addLayer({
			id: LAYER_DRAFT_VERTEX_HIT,
			type: "circle",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "vertex"],
			paint: {
				"circle-radius": 16,
				"circle-opacity": 0,
			},
		});
	}
	if (!map.getLayer(LAYER_DRAFT_MIDPOINT_HIT)) {
		map.addLayer({
			id: LAYER_DRAFT_MIDPOINT_HIT,
			type: "circle",
			source: DRAFT_SOURCE,
			filter: ["==", ["get", "kind"], "midpoint"],
			paint: {
				"circle-radius": 12,
				"circle-opacity": 0,
			},
		});
	}
}

function setSourceData(map: Map, sourceId: string, data: FeatureCollection) {
	const source = map.getSource(sourceId) as GeoJSONSource | undefined;
	if (!source) return;
	source.setData(data as Parameters<GeoJSONSource["setData"]>[0]);
}

function distanceToSegment(
	p: { x: number; y: number },
	a: { x: number; y: number },
	b: { x: number; y: number },
): number {
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	const lengthSq = dx * dx + dy * dy;
	let t = 0;
	if (lengthSq > 0) {
		t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
		t = Math.max(0, Math.min(1, t));
	}
	return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function queryHandleIndex(
	map: Map,
	point: MapMouseEvent["point"],
	layerId: string,
): number | null {
	if (!map.getLayer(layerId)) return null;
	const hits = map.queryRenderedFeatures(point, { layers: [layerId] });
	const index = hits[0]?.properties?.index;
	return typeof index === "number" ? index : null;
}

function queryMidpointHandle(
	map: Map,
	point: MapMouseEvent["point"],
): { index: number; coord: [number, number] } | null {
	if (!map.getLayer(LAYER_DRAFT_MIDPOINT_HIT)) return null;
	const hits = map.queryRenderedFeatures(point, {
		layers: [LAYER_DRAFT_MIDPOINT_HIT],
	});
	const feature = hits[0];
	if (!feature || feature.geometry.type !== "Point") return null;
	const index = feature.properties?.index;
	if (typeof index !== "number") return null;
	const coords = feature.geometry.coordinates as [number, number];
	return { index, coord: [coords[0], coords[1]] };
}

export function MapView({
	className,
	center = [10.45, 51.16],
	zoom = 5.5,
	layers,
	drawMode,
	selectedEntryId,
	draftVertices,
	editing = false,
	viewportCommand,
	onSelectEntry,
	onMapClick,
	onFinishDraft,
	onVertexInsert,
	onVertexRemove,
	onVertexDrag,
	onReady,
}: Props) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const mapRef = useRef<Map | null>(null);
	const readyRef = useRef(false);
	const layersRef = useRef(layers);
	const selectedRef = useRef(selectedEntryId);
	const draftRef = useRef(draftVertices);
	const drawModeRef = useRef(drawMode);
	const editingRef = useRef(editing);
	const lastClickAtRef = useRef(0);
	const callbacksRef = useRef({
		onSelectEntry,
		onMapClick,
		onFinishDraft,
		onVertexInsert,
		onVertexRemove,
		onVertexDrag,
		drawMode,
		editing,
	});
	const viewportCommandRef = useRef(viewportCommand);
	const lastViewportKeyRef = useRef<number | null>(null);
	const dragVertexIndexRef = useRef<number | null>(null);
	const dragMovedRef = useRef(false);
	const dragFromMidpointRef = useRef(false);
	const suppressClickUntilRef = useRef(0);
	layersRef.current = layers;
	selectedRef.current = selectedEntryId;
	draftRef.current = draftVertices;
	drawModeRef.current = drawMode;
	editingRef.current = editing;
	callbacksRef.current = {
		onSelectEntry,
		onMapClick,
		onFinishDraft,
		onVertexInsert,
		onVertexRemove,
		onVertexDrag,
		drawMode,
		editing,
	};
	viewportCommandRef.current = viewportCommand;

	const syncSources = (map: Map) => {
		ensureMapLayers(map);
		ensureAreaLabelImages(map, layersRef.current);
		setSourceData(
			map,
			ENTRIES_SOURCE,
			entriesToFeatureCollection(layersRef.current, selectedRef.current),
		);
		setSourceData(map, LABELS_SOURCE, areaLabelsFeatureCollection(layersRef.current));
		setSourceData(
			map,
			DRAFT_SOURCE,
			draftToFeatureCollection(draftRef.current, drawModeRef.current, editingRef.current),
		);
	};

	const applyViewport = (map: Map, cmd: ViewportCommand) => {
		if (cmd.bbox) {
			const [west, south, east, north] = cmd.bbox;
			if (west === east && south === north) {
				map.flyTo({
					center: [west, south],
					zoom: cmd.zoom ?? 15,
					duration: 900,
				});
				return;
			}
			map.fitBounds(
				[
					[west, south],
					[east, north],
				],
				{ padding: 72, maxZoom: 16, duration: 900 },
			);
			return;
		}

		map.flyTo({
			center: cmd.center,
			zoom: cmd.zoom ?? 14,
			duration: 900,
		});
	};

	useEffect(() => {
		if (!containerRef.current || mapRef.current) return;

		const map = new maplibregl.Map({
			container: containerRef.current,
			style: OPENFREEMAP_STYLE as unknown as StyleSpecification | string,
			center,
			zoom,
			attributionControl: { compact: true },
		});
		map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
		mapRef.current = map;

		const onLoad = () => {
			readyRef.current = true;
			syncSources(map);
			const pending = viewportCommandRef.current;
			if (pending) {
				lastViewportKeyRef.current = pending.key;
				applyViewport(map, pending);
			}
			onReady?.(map);
		};

		const onStyleData = () => {
			if (!map.isStyleLoaded()) return;
			syncSources(map);
		};

		const nearestDraftEdgeIndex = (point: { x: number; y: number }): number | null => {
			const vertices = draftRef.current;
			const mode = drawModeRef.current;
			if (vertices.length < 2 || (mode !== "line" && mode !== "polygon")) return null;
			const closed = mode === "polygon" && vertices.length >= 3;
			const edgeCount = closed ? vertices.length : vertices.length - 1;
			let bestIndex: number | null = null;
			let bestDist = 14;
			for (let i = 0; i < edgeCount; i++) {
				const a = map.project(vertices[i]);
				const b = map.project(vertices[(i + 1) % vertices.length]);
				const dist = distanceToSegment(point, a, b);
				if (dist <= bestDist) {
					bestDist = dist;
					bestIndex = i;
				}
			}
			return bestIndex;
		};

		const beginVertexDrag = (e: MapMouseEvent | MapTouchEvent) => {
			const cb = callbacksRef.current;
			if (!cb.editing) return;
			const vertexIndex = queryHandleIndex(map, e.point, LAYER_DRAFT_VERTEX_HIT);
			if (vertexIndex !== null) {
				dragVertexIndexRef.current = vertexIndex;
				dragMovedRef.current = false;
				dragFromMidpointRef.current = false;
				map.dragPan.disable();
				e.preventDefault();
				return;
			}
			const midpoint = queryMidpointHandle(map, e.point);
			if (midpoint) {
				const insertAt = midpoint.index + 1;
				cb.onVertexInsert?.(insertAt, midpoint.coord);
				dragVertexIndexRef.current = insertAt;
				dragMovedRef.current = false;
				dragFromMidpointRef.current = true;
				map.dragPan.disable();
				e.preventDefault();
			}
		};

		const moveVertexDrag = (e: MapMouseEvent | MapTouchEvent) => {
			const index = dragVertexIndexRef.current;
			if (index === null) return;
			dragMovedRef.current = true;
			callbacksRef.current.onVertexDrag?.(index, [e.lngLat.lng, e.lngLat.lat]);
			map.getCanvas().style.cursor = "grabbing";
			e.preventDefault();
		};

		const endVertexDrag = () => {
			if (dragVertexIndexRef.current === null) return;
			dragVertexIndexRef.current = null;
			if (dragMovedRef.current || dragFromMidpointRef.current) {
				suppressClickUntilRef.current = Date.now() + 350;
			}
			dragMovedRef.current = false;
			dragFromMidpointRef.current = false;
			map.dragPan.enable();
		};

		const handleClick = (e: MapMouseEvent) => {
			const cb = callbacksRef.current;
			const mode = cb.drawMode;

			if (Date.now() < suppressClickUntilRef.current) {
				suppressClickUntilRef.current = 0;
				return;
			}

			if (mode === "select") {
				const hitLayers = [LAYER_POINT_HIT, LAYER_POINT, LAYER_LINE, LAYER_FILL].filter(
					(id) => map.getLayer(id),
				);
				const hits =
					hitLayers.length > 0
						? map.queryRenderedFeatures(e.point, { layers: hitLayers })
						: [];
				const id = hits[0]?.properties?.id;
				cb.onSelectEntry(typeof id === "string" ? id : null);
				return;
			}

			if (cb.editing) {
				const vertexIndex = queryHandleIndex(map, e.point, LAYER_DRAFT_VERTEX_HIT);
				if (vertexIndex !== null) {
					e.preventDefault();
					cb.onVertexRemove?.(vertexIndex);
					return;
				}
				const midpoint = queryMidpointHandle(map, e.point);
				if (midpoint) {
					e.preventDefault();
					cb.onVertexInsert?.(midpoint.index + 1, midpoint.coord);
					return;
				}
				const edgeIndex = nearestDraftEdgeIndex(e.point);
				if (edgeIndex !== null) {
					e.preventDefault();
					cb.onVertexInsert?.(edgeIndex + 1, [e.lngLat.lng, e.lngLat.lat]);
				}
				return;
			}

			const now = Date.now();
			if (now - lastClickAtRef.current < 280 && (mode === "line" || mode === "polygon")) {
				return;
			}
			lastClickAtRef.current = now;

			e.preventDefault();
			cb.onMapClick([e.lngLat.lng, e.lngLat.lat]);
		};

		const handleDblClick = (e: MapMouseEvent) => {
			const mode = callbacksRef.current.drawMode;
			if (mode === "line" || mode === "polygon") {
				e.preventDefault();
				callbacksRef.current.onFinishDraft?.();
			}
		};

		const handleMouseMove = (e: MapLayerMouseEvent | MapMouseEvent) => {
			if (dragVertexIndexRef.current !== null) {
				moveVertexDrag(e);
				return;
			}
			const cb = callbacksRef.current;
			const mode = cb.drawMode;
			if (mode !== "select") {
				if (cb.editing) {
					const overVertex =
						queryHandleIndex(map, e.point, LAYER_DRAFT_VERTEX_HIT) !== null;
					const overMidpoint =
						!overVertex && queryMidpointHandle(map, e.point) !== null;
					const overEdge =
						!overVertex && !overMidpoint && nearestDraftEdgeIndex(e.point) !== null;
					map.getCanvas().style.cursor = overVertex
						? "grab"
						: overMidpoint || overEdge
							? "pointer"
							: "crosshair";
					return;
				}
				map.getCanvas().style.cursor = "crosshair";
				return;
			}
			const hitLayers = [LAYER_POINT_HIT, LAYER_POINT, LAYER_LINE, LAYER_FILL].filter(
				(id) => map.getLayer(id),
			);
			const hits =
				hitLayers.length > 0
					? map.queryRenderedFeatures(e.point, { layers: hitLayers })
					: [];
			map.getCanvas().style.cursor = hits.length > 0 ? "pointer" : "";
		};

		map.on("load", onLoad);
		map.on("styledata", onStyleData);
		map.on("click", handleClick);
		map.on("dblclick", handleDblClick);
		map.on("mousemove", handleMouseMove);
		map.on("mousedown", beginVertexDrag);
		map.on("touchstart", beginVertexDrag);
		map.on("touchmove", moveVertexDrag);
		map.on("mouseup", endVertexDrag);
		map.on("touchend", endVertexDrag);
		map.on("touchcancel", endVertexDrag);
		map.on("mouseout", endVertexDrag);
		map.doubleClickZoom.disable();

		return () => {
			map.off("load", onLoad);
			map.off("styledata", onStyleData);
			map.off("click", handleClick);
			map.off("dblclick", handleDblClick);
			map.off("mousemove", handleMouseMove);
			map.off("mousedown", beginVertexDrag);
			map.off("touchstart", beginVertexDrag);
			map.off("touchmove", moveVertexDrag);
			map.off("mouseup", endVertexDrag);
			map.off("touchend", endVertexDrag);
			map.off("touchcancel", endVertexDrag);
			map.off("mouseout", endVertexDrag);
			map.remove();
			mapRef.current = null;
			readyRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const map = mapRef.current;
		if (!map || !readyRef.current) return;
		syncSources(map);
	}, [layers, selectedEntryId, draftVertices, drawMode, editing]);

	useEffect(() => {
		const map = mapRef.current;
		if (!map || !readyRef.current || !viewportCommand) return;
		if (lastViewportKeyRef.current === viewportCommand.key) return;
		lastViewportKeyRef.current = viewportCommand.key;
		applyViewport(map, viewportCommand);
	}, [viewportCommand]);

	useEffect(() => {
		const map = mapRef.current;
		if (!map) return;
		map.getCanvas().style.cursor = drawMode === "select" ? "" : "crosshair";
	}, [drawMode]);

	return <div ref={containerRef} className={className ?? "h-full w-full"} />;
}

export function geometryFromDraft(
	mode: GeomType,
	vertices: [number, number][],
): GeoJsonGeometry | null {
	if (mode === "point") {
		if (vertices.length < 1) return null;
		return { type: "Point", coordinates: vertices[0] };
	}
	if (mode === "line") {
		if (vertices.length < 2) return null;
		return { type: "LineString", coordinates: vertices };
	}
	if (mode === "polygon") {
		if (vertices.length < 3) return null;
		const ring = [...vertices, vertices[0]];
		return { type: "Polygon", coordinates: [ring] };
	}
	return null;
}
